import { c as _c } from "react/compiler-runtime";
import { useIntl } from "react-intl";
import { useFormattedDatePlaceholders } from "utils/hooks/useFormattedDatePlaceholders";
import type { z } from "zod";
function joinValues<T extends unknown[]>(array: T, separator = " | "): string {
  return array.map(val => typeof val === "string" ? `'${val}'` : val).join(separator);
}
type MakeZodI18nMap = (option?: ZodI18nMapOption) => z.core.$ZodErrorMap;
type ZodI18nMapOption = {
  ns?: string;
  handlePath?: HandlePathOption | false;
};
type HandlePathOption = {
  context?: string;
  ns?: string;
  keyPrefix?: string;
};
export default function useZodErrors() {
  const $ = _c(4);
  const {
    formatMessage
  } = useIntl();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      dateStyle: "short"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const formatDatePlaceholders = useFormattedDatePlaceholders(t0);
  let t1;
  if ($[1] !== formatDatePlaceholders || $[2] !== formatMessage) {
    const makeZodI18nMap = option => {
      const {
        handlePath
      } = {
        ...option,
        handlePath: option?.handlePath !== false ? {
          context: "with_path",
          keyPrefix: undefined,
          ...option?.handlePath
        } : null
      };
      return issue => {
        let message = formatMessage({
          id: "errors.custom"
        });
        const issuePath = issue?.path ?? [];
        const path = issuePath.length > 0 && !!handlePath ? {
          context: handlePath.context,
          path: formatMessage({
            id: [handlePath.keyPrefix, issuePath.join(".")].filter(Boolean).join(".")
          }, {
            defaultMessage: issuePath.join(".")
          })
        } : {};
        bb31: switch (issue.code) {
          case "invalid_type":
            {
              if (issue.input === undefined || issue.input === null) {
                message = formatMessage({
                  id: "errors.invalid_type_received_null"
                }, {
                  defaultMessage: message,
                  ...path
                });
              } else {
                message = formatMessage({
                  id: "errors.invalid_type"
                }, {
                  expected: formatMessage({
                    id: `types.${String(issue.expected)}`
                  }, {
                    defaultMessage: String(issue.expected)
                  }),
                  received: formatMessage({
                    id: `types.${String(issue.input)}`
                  }, {
                    defaultMessage: String(issue.input)
                  }),
                  ...path
                });
              }
              break bb31;
            }
          case "invalid_value":
            {
              message = formatMessage({
                id: "errors.invalid_enum_value"
              }, {
                defaultMessage: message,
                ...path
              });
              break bb31;
            }
          case "unrecognized_keys":
            {
              message = formatMessage({
                id: "errors.unrecognized_keys"
              }, {
                keys: joinValues(issue.keys ?? [], ", "),
                defaultMessage: message,
                ...path
              });
              break bb31;
            }
          case "invalid_union":
            {
              message = formatMessage({
                id: "errors.invalid_union"
              }, {
                defaultMessage: message,
                ...path
              });
              break bb31;
            }
          case "invalid_key":
            {
              message = formatMessage({
                id: "errors.invalid_union_discriminator"
              }, {
                defaultMessage: message,
                ...path
              });
              break bb31;
            }
          case "invalid_format":
            {
              if (issue.format === "starts_with") {
                message = formatMessage({
                  id: "errors.invalid_string.startsWith"
                }, {
                  startsWith: issue.pattern ?? "",
                  defaultMessage: message,
                  ...path
                });
              } else {
                if (issue.format === "ends_with") {
                  message = formatMessage({
                    id: "errors.invalid_string.endsWith"
                  }, {
                    endsWith: issue.pattern ?? "",
                    defaultMessage: message,
                    ...path
                  });
                } else {
                  message = formatMessage({
                    id: "errors.invalid_enum_value"
                  }, {
                    defaultMessage: message,
                    ...path
                  });
                }
              }
              break bb31;
            }
          case "too_small":
            {
              const minimum = issue.origin === "date" ? new Date(Number(issue.minimum)) : issue.minimum;
              const inclusive_0 = issue.inclusive ? "inclusive" : "not_inclusive";
              message = formatMessage({
                id: `errors.too_small.${issue.origin}.${issue.exact ? "exact" : inclusive_0}`
              }, {
                minimum: minimum?.toString(),
                count: typeof minimum === "number" ? minimum : undefined,
                defaultMessage: message,
                ...path
              });
              if (issue.origin === "date" && minimum instanceof Date) {
                message = formatDatePlaceholders(message, {
                  minimum
                });
              }
              break bb31;
            }
          case "too_big":
            {
              const maximum = issue.origin === "date" ? new Date(Number(issue.maximum)) : issue.maximum;
              const inclusive = issue.inclusive ? "inclusive" : "not_inclusive";
              message = formatMessage({
                id: `errors.too_big.${issue.origin}.${issue.exact ? "exact" : inclusive}`
              }, {
                maximum: maximum?.toString(),
                count: typeof maximum === "number" ? maximum : undefined,
                defaultMessage: message,
                ...path
              });
              if (issue.origin === "date" && maximum instanceof Date) {
                message = formatDatePlaceholders(message, {
                  maximum
                });
              }
              break bb31;
            }
          case "not_multiple_of":
            {
              message = formatMessage({
                id: "errors.not_multiple_of"
              }, {
                multipleOf: issue.divisor.toString(),
                defaultMessage: message,
                ...path
              });
              break bb31;
            }
          default:
            {
              message = formatMessage({
                id: "errors.custom"
              }, {
                defaultMessage: message,
                ...path
              });
            }
        }
        return {
          message
        };
      };
    };
    t1 = {
      makeZodI18nMap
    };
    $[1] = formatDatePlaceholders;
    $[2] = formatMessage;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}